// Floor Plan Types for displaying floor plans on the table booking page

export type ReservationItemType =
    | 'UNKNOWN' | 'TABLE' | 'CHAIR' | 'BOOTH' | 'BENCH'
    | 'SUITE' | 'BALCONY' | 'BOX'
    | 'BED' | 'CABANA' | 'LOUNGER'
    | 'JACUZZI' | 'POOL_SECTION';

export const CANVAS_WIDTH = 1200;
export const CANVAS_HEIGHT = 800;

export interface FloorPlanItemResponse {
    id: string;
    floor_plan_id: string;
    booking_category_id: string;
    name: string;
    table_number: string;
    table_size: number;
    item_type: ReservationItemType;
    image_id: string | null;
    shape: 'Rectangle' | 'Circle' | 'Polygon';
    x: number;
    y: number;
    width?: number | null;
    height?: number | null;
    radius?: number | null;
    rotation: number;
    points?: { x: number; y: number }[] | null;
}

export interface FloorPlanResponse {
    id: string;
    name: string;
    business_id: string;
    image_id: string;
    items: FloorPlanItemResponse[];
}

/** Convert a normalized (0–1) value to pixel coordinates on the virtual canvas */
export function toPixel(normalized: number, canvasDimension: number): number {
    return normalized * canvasDimension;
}

/** Compute contain-fit placement for the background image inside the 1200×800 canvas */
export function computeImageFit(imageWidth: number, imageHeight: number) {
    const scaleX = CANVAS_WIDTH / imageWidth;
    const scaleY = CANVAS_HEIGHT / imageHeight;
    const scale = Math.min(scaleX, scaleY);
    const drawWidth = imageWidth * scale;
    const drawHeight = imageHeight * scale;
    const offsetX = (CANVAS_WIDTH - drawWidth) / 2;
    const offsetY = (CANVAS_HEIGHT - drawHeight) / 2;
    return { scale, offsetX, offsetY, drawWidth, drawHeight };
}

/** Corner radius (in virtual canvas pixels) for polygon shapes */
export const POLYGON_CORNER_RADIUS = 10;

/**
 * Generate a rounded-corner SVG path string from a flat points array.
 * Uses Quadratic Bezier curves at each vertex to create smooth corners
 * while keeping edges perfectly straight.
 *
 * @param points  Flat array [x1, y1, x2, y2, ...]
 * @param radius  Desired corner radius (auto-clamped to half shortest edge)
 * @returns       SVG path `d` attribute string
 */
export const getRoundedPolygonPath = (points: number[], radius: number): string => {
    const polyPoints: { x: number; y: number }[] = [];
    for (let i = 0; i < points.length; i += 2) {
        polyPoints.push({ x: points[i], y: points[i + 1] });
    }

    if (polyPoints.length < 3) return "";

    let d = "";
    const len = polyPoints.length;

    for (let i = 0; i < len; i++) {
        const pPrev = polyPoints[i === 0 ? len - 1 : i - 1];
        const pCurr = polyPoints[i];
        const pNext = polyPoints[i === len - 1 ? 0 : i + 1];

        const vPrev = { x: pPrev.x - pCurr.x, y: pPrev.y - pCurr.y };
        const vNext = { x: pNext.x - pCurr.x, y: pNext.y - pCurr.y };

        const distPrev = Math.sqrt(vPrev.x ** 2 + vPrev.y ** 2);
        const distNext = Math.sqrt(vNext.x ** 2 + vNext.y ** 2);

        const r = Math.min(radius, distPrev / 2, distNext / 2);

        const nPrev = distPrev > 0.001
            ? { x: vPrev.x / distPrev, y: vPrev.y / distPrev }
            : { x: 0, y: 0 };
        const nNext = distNext > 0.001
            ? { x: vNext.x / distNext, y: vNext.y / distNext }
            : { x: 0, y: 0 };

        const start = { x: pCurr.x + nPrev.x * r, y: pCurr.y + nPrev.y * r };
        const end = { x: pCurr.x + nNext.x * r, y: pCurr.y + nNext.y * r };

        if (i === 0) d += `M ${start.x},${start.y} `;
        else d += `L ${start.x},${start.y} `;

        d += `Q ${pCurr.x},${pCurr.y} ${end.x},${end.y} `;
    }

    d += "Z";
    return d;
};
